import React, { useEffect, useState } from "react";
import useInput, { TypeInput } from "../../assets/hooks/use-input";
import "./AuthForm.scss";
import IconEye from "../../assets/components/Inputs/TextInput/Img/eye.svg";
import ActionButtons from "../../assets/components/ActionButtons/ActionButtons";
import { fetchAuthToken } from "../../redux/auth-action";
import { useAppDispatch, useAppSelector } from "../../assets/hooks/hooks";
import { useNavigate } from "react-router-dom";
import { authActions } from "../../redux/auth-slice";
import { sha256 } from "js-sha256";

const AuthForm = (props) => {
    const { loading, errorMessage } = useAppSelector((state) => state.auth);
    const navigate = useNavigate();

    const [isCapsLockOn, setIsCapsLockOn] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    const dispatch = useAppDispatch();

    const toggleTypePassword = (event) => {
        event.preventDefault();
        setShowPassword(!showPassword);
    };

    const {
        value: enteredName,
        isValid: enteredNameIsValid,
        hasError: nameInputHasError,
        valueChangeHandler: nameChangeHandler,
        inputBlurHandler: nameBlurHandler,
        reset: resetNameInput,
        errorMessage: nameErrorMessage,
    } = useInput();

    const {
        value: enteredPassword,
        isValid: enteredPasswordIsValid,
        hasError: passwordInputHasError,
        valueChangeHandler: passwordChangeHandler,
        inputBlurHandler: passwordBlurHandler,
        reset: resetPasswordInput,
        errorMessage: passwordErrorMessage,
    } = useInput();

    const checkCapsLock = (event) => {
        if (event.getModifierState("CapsLock")) {
            setIsCapsLockOn(true);
        } else {
            setIsCapsLockOn(false);
        }
    };

    let formIsValid = false;
    formIsValid = enteredNameIsValid && enteredPasswordIsValid;

    if (loading === "pending") {
        formIsValid = false;
    }

    useEffect(() => {
        if (loading === "succeeded") {
            dispatch(authActions.authStateSucceeded());
            navigate("/");
        }
    }, [loading, navigate, dispatch]);

    const submitHandler = async (event) => {
        event.preventDefault();

        if (!formIsValid) {
            return;
        }

        const formData = {
            name: enteredName,
            password: sha256(enteredPassword),
        };

        dispatch(fetchAuthToken(formData)).then(
            () => {
                resetNameInput();
                resetPasswordInput();
            },
            (reason) => {
                dispatch(authActions.authStateFailed());
            }
        );
    };

    const inputRef = React.useRef(null);

    const handleClick = () => {
        if (inputRef && inputRef.current) inputRef.current.focus();
    };

    const availableInputClasses = (isError, isValid) => {
        if (isError) {
            return "form_control invalid";
        }
        if (isValid) {
            return "form_control valid";
        }
        return "form_control";
    };

    const getAvailableErrorMessage = () => {
        if (passwordInputHasError) {
            return (
                <p className="form_control__error">{passwordErrorMessage}</p>
            );
        } else if (loading === "failed" && !enteredPasswordIsValid) {
            return <p className="form_control__error">{errorMessage}</p>;
        }

        // return <p className="form_control__forgot_password">Forgot password?</p>
    };

    return (
        <form className="auth_form" onSubmit={submitHandler}>
            <h1 className="auth_form__title">LOGIN</h1>

            <div
                className={availableInputClasses(
                    nameInputHasError,
                    enteredNameIsValid
                )}
                onClick={handleClick}
            >
                <input
                    className="form_control__input"
                    type="text"
                    name={TypeInput.NAME}
                    id={TypeInput.NAME}
                    onChange={nameChangeHandler}
                    onBlur={nameBlurHandler}
                    value={enteredName}
                    placeholder="Username"
                    maxLength={40}
                    autoComplete="username"
                />
                <span className="animation-bg"></span>

                <label className="form_control__label" htmlFor={TypeInput.NAME}>
                    <span>Username</span>
                </label>

                {nameInputHasError && (
                    <p className="form_control__error">{nameErrorMessage}</p>
                )}
            </div>

            <div
                className={availableInputClasses(
                    passwordInputHasError,
                    enteredPasswordIsValid
                )}
                type={TypeInput.PASSWORD}
                onClick={handleClick}
            >
                <input
                    className="form_control__input"
                    type={showPassword ? "text" : "password"}
                    name={TypeInput.PASSWORD}
                    id={TypeInput.PASSWORD}
                    onKeyDown={checkCapsLock}
                    onChange={passwordChangeHandler}
                    onBlur={passwordBlurHandler}
                    value={enteredPassword}
                    placeholder="Password"
                    maxLength={26}
                    autoComplete="current-password"
                />
                <span className="animation-bg"></span>

                <label
                    className="form_control__label"
                    htmlFor={TypeInput.PASSWORD}
                >
                    <span>Password </span>
                </label>

                <div className="action_panel">
                    {isCapsLockOn && (
                        <div className="action_panel__text">CAPS</div>
                    )}
                    {enteredPassword && (
                        <button
                            onClick={toggleTypePassword}
                            className="action_panel__button"
                        >
                            <img src={IconEye} alt="Eye button img" />
                        </button>
                    )}
                </div>

                {getAvailableErrorMessage()}
            </div>

            <ActionButtons
                className="form_control__button_submit"
                actionButtons={[
                    {
                        type: "submit",
                        name: "login",
                        role: "primary",
                        disabled: !formIsValid,
                    },
                ]}
            />
        </form>
    );
};

export default AuthForm;
